'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import type { GraphQLContactPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useContactsSettings } from '@sola/supporter-sdk/hooks/settings.ts';
import { CommsOptionEnum, CommsPreferenceEnum } from '../../molecules/comms-preference-row/comms-preference-row.logic.ts';
export type CommsPreferencesState = Record<string, CommsOptionEnum[]>;
export type CommsPreferencesFormValues = Partial<Record<CommsPreferenceEnum, Partial<Record<CommsOptionEnum, boolean | undefined>>>>;
interface PreferenceChannelConfig {
  channels: CommsOptionEnum[];
  isDisabled?: boolean;
}

/**
 * Any category coming from the server for a preference/channel that isn't
 * listed here is intentionally dropped so users can't be locked into
 * selections they can't see or edit.
 */
export const preferenceChannels: Record<CommsPreferenceEnum, PreferenceChannelConfig> = {
  [CommsPreferenceEnum.AccountTransactions]: {
    channels: [CommsOptionEnum.Email, CommsOptionEnum.Text],
    isDisabled: true
  },
  [CommsPreferenceEnum.ChildUpdates]: {
    channels: [CommsOptionEnum.Email, CommsOptionEnum.Text]
  },
  [CommsPreferenceEnum.YourImpact]: {
    channels: [CommsOptionEnum.Email, CommsOptionEnum.Text]
  },
  [CommsPreferenceEnum.Communications]: {
    channels: [CommsOptionEnum.Email]
  },
  [CommsPreferenceEnum.HelpUsImprove]: {
    channels: [CommsOptionEnum.Email]
  },
  [CommsPreferenceEnum.PrayerNetwork]: {
    channels: [CommsOptionEnum.Email]
  },
  [CommsPreferenceEnum.ExplorerMagazine]: {
    channels: [CommsOptionEnum.Email]
  },
  [CommsPreferenceEnum.VolunteeringVisits]: {
    channels: [CommsOptionEnum.Email]
  }
};
const preferenceEntries = Object.entries(preferenceChannels) as [CommsPreferenceEnum, PreferenceChannelConfig][];
const categoryToPreference = new Map<string, CommsPreferenceEnum>([['Account_Transaction', CommsPreferenceEnum.AccountTransactions], ['Sponsored_Child_Communication', CommsPreferenceEnum.ChildUpdates], ['Your_Impact', CommsPreferenceEnum.YourImpact], ['Compassion_Communication', CommsPreferenceEnum.Communications], ['Help_Us_Improve', CommsPreferenceEnum.HelpUsImprove], ['Compassion_Prayer_Network', CommsPreferenceEnum.PrayerNetwork], ['Compassion_Explorer_Magazine_for_Kids', CommsPreferenceEnum.ExplorerMagazine], ['Volunteering_and_Visits', CommsPreferenceEnum.VolunteeringVisits]]);
const addSelection = (selections: CommsPreferencesState, preference: CommsPreferenceEnum, option: CommsOptionEnum) => {
  if (!preferenceChannels[preference].channels.includes(option)) {
    // Drop server categories that aren't exposed for this channel in the UI.
    return;
  }
  selections[preference] = [...(selections[preference] ?? []), option];
};
const buildCommsInitialSelections = (preferences?: GraphQLContactPreference | null): CommsPreferencesState => {
  const selections = Object.fromEntries(preferenceEntries.map(([preference]) => [preference, []])) as CommsPreferencesState;
  for (const category of preferences?.emailInterestCategories ?? []) {
    const preference = categoryToPreference.get(category);
    if (preference) {
      addSelection(selections, preference, CommsOptionEnum.Email);
    }
  }
  for (const category of preferences?.textInterestCategories ?? []) {
    const preference = categoryToPreference.get(category);
    if (preference) {
      addSelection(selections, preference, CommsOptionEnum.Text);
    }
  }
  return selections;
};
const buildFormDefaultValues = (selections: CommsPreferencesState): CommsPreferencesFormValues => Object.fromEntries(preferenceEntries.map(([preference, {
  channels
}]) => {
  const selectedOptions = selections[preference] ?? [];
  return [preference, Object.fromEntries(channels.map(channel => [channel, selectedOptions.includes(channel)]))];
})) as CommsPreferencesFormValues;
export interface UseCommsPreferencesSheetProps {
  contactId: string;
}
export const useCommsPreferencesSheet = t0 => {
  const $ = _c(16);
  const {
    contactId
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving] = useState(false);
  const {
    contacts
  } = useContactsSettings();
  let t1;
  if ($[0] !== contactId || $[1] !== contacts) {
    t1 = contacts?.find(t2 => {
      const {
        id
      } = t2;
      return id === contactId;
    });
    $[0] = contactId;
    $[1] = contacts;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const contact = t1;
  let t2;
  if ($[3] !== contact?.preferences) {
    t2 = () => buildCommsInitialSelections(contact?.preferences);
    $[3] = contact?.preferences;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  contact?.preferences;
  let t3;
  if ($[5] !== t2) {
    t3 = t2();
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const initialSelections = t3;
  let t4;
  if ($[7] !== initialSelections) {
    t4 = buildFormDefaultValues(initialSelections);
    $[7] = initialSelections;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const formDefaultValues = t4;
  const supporterName = contact?.firstName ?? contact?.lastName ?? "";
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = async _values => {
      setIsOpen(false);
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSave = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = open => {
      setIsOpen(open);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[11] !== formDefaultValues || $[12] !== isOpen || $[13] !== isSaving || $[14] !== supporterName) {
    t7 = {
      formDefaultValues,
      isOpen,
      isSaving,
      supporterName,
      handleSave,
      handleOpenChange
    };
    $[11] = formDefaultValues;
    $[12] = isOpen;
    $[13] = isSaving;
    $[14] = supporterName;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};